// Soft upgrade nudge for free users who have already used their free builds.
//
// Shows once per browser session (sessionStorage) when:
//   - user is signed in
//   - plan === 'free'
//   - completedBuilds >= the free limit
// User can "Maybe later" to dismiss for the session. Login is treated as the
// boundary — the prompt re-arms after sign-out/sign-in.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useBuildCredits } from '@/hooks/use-build-credits';
import { PLAN_CONFIG } from '@/lib/plan-config';

const DISMISS_KEY = 'upgrade_prompt_dismissed_session';
// Routes where the modal would be in the way — these are conversion pages or
// flows where popping a nag screen would feel hostile.
const SKIP_PATHS = ['/auth', '/subscription', '/verify-success', '/extension/authorize'];

export const UpgradePromptOverlay = () => {
  const { user } = useAuth();
  const { stats, loading } = useBuildCredits();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loading || !user || !stats) return;
    if (stats.plan !== 'free') return;
    if (stats.completedBuilds < PLAN_CONFIG.limits.free) return;
    if (SKIP_PATHS.some((p) => location.pathname.startsWith(p))) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    setOpen(true);
  }, [user, stats, loading, location.pathname]);

  const handleDismiss = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const handleUpgrade = () => {
    sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
    navigate('/subscription');
  };

  return (
    <Dialog open={open} onOpenChange={handleDismiss}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Sparkles className="w-12 h-12 text-primary" />
          </div>
          <DialogTitle className="text-xl text-center">
            Welcome back — ready for more?
          </DialogTitle>
          <DialogDescription className="text-center text-base pt-2">
            You've used your {PLAN_CONFIG.limits.free} free builds. Upgrade to
            Starter for {PLAN_CONFIG.limits.starter} builds/month and keep
            shipping prompts.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 mt-4">
          <Button onClick={handleUpgrade} className="w-full" size="lg">
            <Sparkles className="w-4 h-4 mr-2" />
            See plans
          </Button>
          <Button variant="ghost" onClick={() => handleDismiss(false)} className="w-full">
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
